import { axiosPost } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import { authenticationQueryKeyMap } from '@/features/authentication/_utils/query-keys';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';

import {
  ApprovalDecisionRequestSchema,
  ApprovalReviewRequestSchema,
} from '../models/approval';
import { workflowQueryKeyMap } from '../utils/workflow-query-keys';

export const useApprovalDecision = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ApprovalDecisionRequestSchema>,
    ) => {
      const { id, type, workflowId, stageId, ...payload } =
        ApprovalDecisionRequestSchema.parse(values);
      return await axiosPost(
        `/api/workflows/${workflowId}/stages/${stageId}/approvals/${id}/${type}/`,
        {
          ...payload,
        },
      );
    },
    onSuccess: async (_, { workflowId }) => {
      await Promise.all([
        invalidate(workflowQueryKeyMap.workflowList()),
        invalidate(workflowQueryKeyMap.workflow(workflowId)),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
      ]);
    },
  });
};

export const useApprovalReview = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof ApprovalReviewRequestSchema>) => {
      const { id, workflowId, stageId, ...payload } =
        ApprovalReviewRequestSchema.parse(values);
      const res = await axiosPost(
        `/api/workflows/${workflowId}/stages/${stageId}/approvals/${id}/review/`,
        {
          ...payload,
        },
      );
      return res;
    },
    onSuccess: async (_, { workflowId }) => {
      await Promise.all([
        invalidate(workflowQueryKeyMap.workflowList()),
        invalidate(workflowQueryKeyMap.workflow(workflowId)),
        invalidate(authenticationQueryKeyMap.myNotificationBadges()),
      ]);
    },
  });
};
